import "./App.css";
import KinesisWebRTC from "./components/kinesis-webrtc";
import React, { useEffect, useState } from "react";
import { store, view } from "@risingstack/react-easy-state";
import { decryptToken } from "./utils/tokenDecrypt";
import { initializeSecurityMeasures } from "./utils/securityUtils";
import { initializeConfig } from "./config";

// Create a simple state store
const state = store({
  role: "VIEWER",
  channelName: "",
  studentId: "",
  isStreamStarted: false,
  examId: "",
  universityId: "",
});

function App() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initialize = async () => {
      try {
        setIsLoading(true);
        // Initialize AWS config first
        await initializeConfig();

        // Initialize security measures
        initializeSecurityMeasures();

        // For testing multiple viewers
        const urlParams = new URLSearchParams(window.location.search);
        let token = urlParams.get("token");

        if (token) {
          // Decrypt token
          token = token.replace(/ /g, "+");
          const decryptedData = decryptToken(token);
          console.log("Decrypted token data:", decryptedData);

          if (decryptedData) {
            // Set state with decrypted values
            state.channelName = decryptedData.channelName;
            state.studentId = decryptedData.studentId;
            state.examId = decryptedData.examId;

            // Fetch universityId
            try {
              const response = await fetch(
                `https://procbackend.elcarreira.com/students/getUniversityId?studentId=${decryptedData.studentId}`
              );
              if (!response.ok) {
                throw new Error("Failed to fetch universityId");
              }
              const data = await response.json();
              state.universityId = data.data;
              console.log("Fetched universityId:", state.universityId);
            } catch (error) {
              console.error("Error fetching universityId:", error);
              throw new Error("Failed to fetch universityId");
            }

            // Set window.appState for kinesis-webrtc.js
            window.appState = {
              role: "VIEWER",
              channelName: decryptedData.channelName,
              clientId: decryptedData.studentId,
              autoStart: true,
              examId: decryptedData.examId,
              startTime: decryptedData.startTime,
              endTime: decryptedData.endTime,
              universityId: state.universityId,
            };

            localStorage.setItem("examId", decryptedData.examId);
            // Mark as initialized
            console.log("App state initialized:", {
              examId: state.examId,
              universityId: state.universityId,
              studentId: state.studentId,
            });
            setIsInitialized(true);
          } else {
            throw new Error("Failed to decrypt token");
          }
        } else {
          throw new Error("No token provided in URL");
        }
      } catch (error) {
        console.error("Initialization error:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, []);

  const stopStream = () => {
    state.isStreamStarted = false;
    window.appState = null;
  };

  // Show loading state until initialized
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Initializing connection...</div>
      </div>
    );
  }

  // Show error state if initialization failed
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-red-600">Error: {error}</div>
      </div>
    );
  }

  // Show loading state until initialized
  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Waiting for initialization...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="p-4">
        <div className="max-w-6xl mx-auto">
          <KinesisWebRTC onStreamStopped={stopStream} />
        </div>
      </div>
    </div>
  );
}
export default view(App);
